// Trigger + modal-chrome editor for a POP-UP SECTION (`section.popup`).
//
// A section tagged `popup` is not rendered in the page flow — it renders a
// single trigger that opens the section's OWN blocks in a modal. The author
// adds those blocks on the canvas like any other section; this editor only
// configures the trigger (style / label / icon / placement) and the modal
// chrome (title + size). It is shown inside SectionPropertiesEditor when the
// selected section is a pop-up.
//
// Field map (SectionPopup / PopupTrigger — see @twasta/page-schema):
//   trigger.style    → select (fab | link | icon)
//   trigger.label    → text (fab/link only)
//   trigger.icon     → text (lucide/emoji)
//   trigger.variant  → select (primary | secondary | outline | ghost | link)
//   trigger.position → select (corner; fab/icon only)
//   trigger.offset   → number (px from corner; fab/icon only)
//   trigger.size     → select (sm | md | lg)
//   trigger.shape    → select (pill | rounded | circle)
//   trigger.bgColor  → color (overrides variant background)
//   trigger.textColor→ color (overrides variant text/icon)
//   trigger.iconSize → number (px glyph size)
//   trigger.shadow   → checkbox (drop shadow on fab/icon)
//   trigger.pulse    → checkbox (attention-pulse animation)
//   modalTitle       → text
//   modalSize        → select (sm | md | lg)
//   modalPosition    → select (center | top | bottom | left | right)
//   animation        → select (none | fade | scale | slide)

import type { SectionPopup, PopupTrigger } from './types'

interface Props {
  value: SectionPopup | undefined
  onChange: (next: SectionPopup) => void
}

const DEFAULT_POPUP: SectionPopup = {
  trigger: { style: 'fab', label: 'Help', icon: '💬', variant: 'primary', position: 'bottom-right', offset: 24 },
  modalSize: 'md',
}

const STYLE_OPTIONS: { value: PopupTrigger['style']; label: string }[] = [
  { value: 'fab', label: 'Floating button' },
  { value: 'link', label: 'Inline link / button' },
  { value: 'icon', label: 'Icon-only badge' },
]

const VARIANT_OPTIONS: NonNullable<PopupTrigger['variant']>[] = [
  'primary', 'secondary', 'outline', 'ghost', 'link',
]

const POSITION_OPTIONS: NonNullable<PopupTrigger['position']>[] = [
  'bottom-right', 'bottom-left', 'top-right', 'top-left',
]

const SIZE_OPTIONS: { value: NonNullable<SectionPopup['modalSize']>; label: string }[] = [
  { value: 'sm', label: 'Small (480px)' },
  { value: 'md', label: 'Medium (720px)' },
  { value: 'lg', label: 'Large (960px)' },
]

const TRIGGER_SIZE_OPTIONS: { value: NonNullable<PopupTrigger['size']>; label: string }[] = [
  { value: 'sm', label: 'Small' },
  { value: 'md', label: 'Medium' },
  { value: 'lg', label: 'Large' },
]

const SHAPE_OPTIONS: { value: NonNullable<PopupTrigger['shape']>; label: string }[] = [
  { value: 'pill', label: 'Pill (fully rounded)' },
  { value: 'rounded', label: 'Rounded corners' },
  { value: 'circle', label: 'Circle' },
]

const MODAL_POSITION_OPTIONS: { value: NonNullable<SectionPopup['modalPosition']>; label: string }[] = [
  { value: 'top', label: 'Top (centered)' },
  { value: 'center', label: 'Center' },
  { value: 'bottom', label: 'Bottom sheet' },
  { value: 'left', label: 'Left drawer' },
  { value: 'right', label: 'Right drawer' },
]

const ANIMATION_OPTIONS: { value: NonNullable<SectionPopup['animation']>; label: string }[] = [
  { value: 'scale', label: 'Scale / pop' },
  { value: 'fade', label: 'Fade' },
  { value: 'slide', label: 'Slide (from placement)' },
  { value: 'none', label: 'None' },
]

export function SectionPopupEditor({ value, onChange }: Props) {
  const popup: SectionPopup = value ?? DEFAULT_POPUP
  const trigger: PopupTrigger = popup.trigger || DEFAULT_POPUP.trigger
  const setTrigger = (patch: Partial<PopupTrigger>) =>
    onChange({ ...popup, trigger: { ...trigger, ...patch } })
  const set = (patch: Partial<SectionPopup>) => onChange({ ...popup, ...patch })

  const isCornered = trigger.style !== 'link'   // fab + icon anchor to a corner
  const showsLabel = trigger.style !== 'icon'   // icon-only hides the label

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <p style={hintStyle}>
        This section opens in a pop-up modal. Add blocks to it on the canvas like any other
        section — whatever you add (a Chatbot, an Embed of a settings page, text, a form…)
        becomes the modal content.
      </p>

      <Field label="Trigger style">
        <select
          value={trigger.style}
          onChange={(e) => setTrigger({ style: e.target.value as PopupTrigger['style'] })}
          style={inputStyle}
        >
          {STYLE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <p style={hintStyle}>How the end-user opens the pop-up. All three open the same modal.</p>
      </Field>

      {showsLabel && (
        <Field label="Label">
          <input
            type="text"
            value={trigger.label ?? ''}
            onChange={(e) => setTrigger({ label: e.target.value })}
            placeholder="Help"
            style={inputStyle}
          />
        </Field>
      )}

      <Field label="Icon (lucide name or emoji)">
        <input
          type="text"
          value={trigger.icon ?? ''}
          onChange={(e) => setTrigger({ icon: e.target.value || undefined })}
          placeholder="💬"
          style={inputStyle}
        />
        <p style={hintStyle}>Optional for floating/inline; recommended for the icon-only badge.</p>
      </Field>

      <Field label="Button style">
        <select
          value={trigger.variant ?? 'primary'}
          onChange={(e) => setTrigger({ variant: e.target.value as PopupTrigger['variant'] })}
          style={inputStyle}
        >
          {VARIANT_OPTIONS.map((v) => <option key={v} value={v}>{v}</option>)}
        </select>
      </Field>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <Field label="Size">
          <select
            value={trigger.size ?? 'md'}
            onChange={(e) => setTrigger({ size: e.target.value as PopupTrigger['size'] })}
            style={inputStyle}
          >
            {TRIGGER_SIZE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </Field>

        <Field label="Shape">
          <select
            value={trigger.shape ?? (trigger.style === 'icon' ? 'circle' : 'pill')}
            onChange={(e) => setTrigger({ shape: e.target.value as PopupTrigger['shape'] })}
            style={inputStyle}
          >
            {SHAPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </Field>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <Field label="Background color">
          <ColorField
            value={trigger.bgColor}
            onChange={(v) => setTrigger({ bgColor: v })}
            placeholder="Theme default"
          />
        </Field>
        <Field label="Text / icon color">
          <ColorField
            value={trigger.textColor}
            onChange={(v) => setTrigger({ textColor: v })}
            placeholder="Theme default"
          />
        </Field>
      </div>

      <Field label="Icon size (px)">
        <input
          type="number"
          min={10}
          max={64}
          value={trigger.iconSize ?? ''}
          onChange={(e) => {
            const v = parseInt(e.target.value, 10)
            setTrigger({ iconSize: Number.isFinite(v) ? v : undefined })
          }}
          placeholder="Auto (scales with size)"
          style={inputStyle}
        />
      </Field>

      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        <label style={checkboxRowStyle}>
          <input
            type="checkbox"
            checked={trigger.shadow !== false}
            onChange={(e) => setTrigger({ shadow: e.target.checked })}
          />
          Drop shadow
        </label>
        <label style={checkboxRowStyle}>
          <input
            type="checkbox"
            checked={!!trigger.pulse}
            onChange={(e) => setTrigger({ pulse: e.target.checked })}
          />
          Attention pulse
        </label>
      </div>

      {isCornered && (
        <>
          <Field label="Screen position">
            <select
              value={trigger.position ?? 'bottom-right'}
              onChange={(e) => setTrigger({ position: e.target.value as PopupTrigger['position'] })}
              style={inputStyle}
            >
              {POSITION_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
            <p style={hintStyle}>Corner the floating trigger is anchored to.</p>
          </Field>

          <Field label="Offset from corner (px)">
            <input
              type="number"
              min={0}
              max={200}
              value={trigger.offset ?? 24}
              onChange={(e) => {
                const v = parseInt(e.target.value, 10)
                if (!Number.isFinite(v)) return
                setTrigger({ offset: v })
              }}
              style={inputStyle}
            />
          </Field>
        </>
      )}

      <div style={sectionStyle}>
        <Field label="Modal title">
          <input
            type="text"
            value={popup.modalTitle ?? ''}
            onChange={(e) => set({ modalTitle: e.target.value })}
            placeholder="(defaults to the trigger label)"
            style={inputStyle}
          />
        </Field>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <Field label="Modal size">
            <select
              value={popup.modalSize ?? 'md'}
              onChange={(e) => set({ modalSize: e.target.value as SectionPopup['modalSize'] })}
              style={inputStyle}
            >
              {SIZE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </Field>

          <Field label="Placement">
            <select
              value={popup.modalPosition ?? 'top'}
              onChange={(e) => set({ modalPosition: e.target.value as SectionPopup['modalPosition'] })}
              style={inputStyle}
            >
              {MODAL_POSITION_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </Field>
        </div>

        <Field label="Open animation">
          <select
            value={popup.animation ?? 'scale'}
            onChange={(e) => set({ animation: e.target.value as SectionPopup['animation'] })}
            style={inputStyle}
          >
            {ANIMATION_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <p style={hintStyle}>Plays when the trigger is clicked. “Slide” enters from the modal’s placement edge.</p>
        </Field>
      </div>
    </div>
  )
}

// Color picker + hex input with a clear button (empty = inherit theme).
function ColorField({ value, onChange, placeholder }: {
  value?: string
  onChange: (v: string | undefined) => void
  placeholder?: string
}) {
  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
      <input
        type="color"
        value={value || '#000000'}
        onChange={(e) => onChange(e.target.value)}
        style={{ width: 32, height: 30, padding: 0, border: '1px solid var(--color-border)', borderRadius: 4, background: 'none', cursor: 'pointer' }}
        aria-label="Pick color"
      />
      <input
        type="text"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value || undefined)}
        placeholder={placeholder}
        style={{ ...inputStyle, flex: 1 }}
      />
      {value && (
        <button
          type="button"
          onClick={() => onChange(undefined)}
          title="Clear"
          style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--color-text-secondary)', fontSize: 17, lineHeight: 1, padding: '0 2px' }}
        >
          ×
        </button>
      )}
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'grid', gap: 4 }}>
      <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>{label}</span>
      {children}
    </div>
  )
}

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '6px 8px', fontSize: 13,
  border: '1px solid var(--color-border-strong)', borderRadius: 4,
  background: 'var(--color-input-bg)', color: 'var(--color-text)',
}
const hintStyle: React.CSSProperties = {
  margin: '4px 0 0', fontSize: 13, color: 'var(--color-text-secondary)', lineHeight: 1.5,
}
const sectionStyle: React.CSSProperties = {
  borderTop: '1px solid var(--color-border)', paddingTop: 10, display: 'grid', gap: 12,
}
const checkboxRowStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--color-text)', cursor: 'pointer',
}
